import { useEffect, useState } from 'react';

import './Table.css';

function mapIndexes(n, fn) {
    let arr = [];
    for(let i = 0; i < n; i++) {
        arr.push(fn(i));
    }
    return arr;
}

/**
 * Server-driven table: search, sorting and pagination are all handled by the
 * backend (via limit/offset). The parent owns the page/search/sort state and
 * re-fetches whenever it changes; this component only renders the current page
 * and reports user intent through callbacks.
 *
 * Layout/markup mirror the client-side <Table> (shares Table.css) so the two
 * look identical. The generic <Table> is left untouched for every other table.
 */
function ServerTable({
    columnNames,
    columnRenderers,
    serverSortKeys = [],
    data = [],
    total = 0,
    pageSize = 10,
    page = 0,
    search = '',
    sortKey = '',
    sortDir = '',
    showAll = false,
    onPageChange = () => {},
    onSearchChange = () => {},
    onSortChange = () => {},
    onToggleShowAll = () => {},
    style = {},
    showSearch = true,
}) {
    // Local mirror of the search box so we can debounce network calls.
    const [searchInput, setSearchInput] = useState(search);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if(searchInput !== search) onSearchChange(searchInput);
        }, 350);
        return () => clearTimeout(timeout);
    }, [searchInput]);

    const numPages = Math.max(1, Math.ceil(total / pageSize));
    const canPage = total > pageSize;
    const currentlyPaging = canPage && !showAll;

    const nearbyPages = [page - 2, page - 1, page, page + 1, page + 2].filter(p => p >= 0 && p < numPages);
    const firstPadding = Math.max(0, 2 - page);
    const lastPadding = Math.max(0, 2 - (numPages - 1 - page));

    const firstPage = () => onPageChange(0);
    const lastPage = () => onPageChange(numPages - 1);
    const nextPage = () => onPageChange(Math.min(page + 1, numPages - 1));
    const prevPage = () => onPageChange(Math.max(page - 1, 0));

    // Tri-state header cycle: none -> asc -> desc -> none
    const sortBy = (i) => {
        const key = serverSortKeys[i];
        if(!key) return;
        if(key === sortKey) {
            if(sortDir === 'asc') onSortChange(key, 'desc');
            else onSortChange('', '');
        } else {
            onSortChange(key, 'asc');
        }
    };

    const dataLength = data ? data.length : 0;

    return (
        <div className="data-table column g8" style={{ width: '100%', overflowX: 'hidden' }}>
            { showSearch && (
                <div className="row g8" style={{'justifyContent': 'flex-end'}}>
                    <input type="text" placeholder="Otsi..." value={searchInput} onChange={e => setSearchInput(e.target.value)} style={{ maxWidth: '256px', width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc' }} />
                </div>
            )}
            <div style={{ width: '100%', paddingBottom: 16 }}>
                <div className='table-wrapper' style={{ width: '100%', overflowX: 'scroll' }}>
                    <table style={{ ...style, width: 'max-content', minWidth: '100%' }}>
                        <thead>
                            <tr>
                                { columnNames.map(
                                    (item, i) => (serverSortKeys && serverSortKeys[i]) ? (
                                        <th key={i} onClick={() => sortBy(i)} style={sortKey == serverSortKeys[i] ? {backgroundColor: "#d8d8d8"} : {}}>{item} {
                                            sortKey == serverSortKeys[i] ? (
                                                sortDir === 'desc' ? '▼' : '▲'
                                            ) : (
                                                <span style={{color: "#aaa"}}>▼</span>
                                            )
                                        }</th>
                                    ) : (
                                        <th key={i}>{item}</th>
                                    )
                                ) }
                            </tr>
                        </thead>
                        <tbody>
                            { dataLength ? data.map((row, ri) => (
                                <tr key={ri}>
                                    { mapIndexes(columnNames.length, (i) => (
                                        <td key={i}>
                                            { columnRenderers[i](row) }
                                        </td>
                                    )) }
                                </tr>
                            )) : (
                                <tr>
                                    <td align='center' colSpan={columnNames.length}>
                                        <p style={{ color: '#888' }}>{(showSearch && search) ? "0 tulemust" : "Sisu puudub"}</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="navbar row g8 center-cross">
                    { canPage && (
                        <button className='small-round' onClick={onToggleShowAll}>{showAll ? 'Kuva lehtedena' : 'Kuva kõik'}</button>
                    ) }

                    { currentlyPaging && (
                        <p className='p8' style={{ color: '#444', textAlign: 'center' }}>Leht {page + 1} / {numPages}-st (kokku {total} rida)</p>
                    ) }

                    <div className='grow'></div>

                    { currentlyPaging ? ( <>
                        <button style={{ color: '#444' }} className='table-scroll-button noselect' onClick={firstPage}><svg width="12" height="12" viewBox="0 0 24 24"><line x1="18" y1="4" x2="6" y2="12" stroke="#888" stroke-width="2" /><line x1="18" y1="20" x2="6" y2="12" stroke="#888" stroke-width="2" /><line x1="4" y1="0" x2="4" y2="24" stroke="#888" stroke-width="2" /></svg></button>
                        <button style={{ color: '#444' }} className='table-scroll-button noselect' onClick={prevPage}><svg width="12" height="12" viewBox="0 0 24 24"><line x1="18" y1="4" x2="6" y2="12" stroke="#888" stroke-width="2" /><line x1="18" y1="20" x2="6" y2="12" stroke="#888" stroke-width="2" /></svg></button>

                        { mapIndexes(firstPadding, i => (
                            <div key={'fp'+i} style={{ width: 24 }}></div>
                        )) }
                        { nearbyPages.map(p => (
                            <button key={p} style={{ width: 24, color: '#444', backgroundColor: (page == p) ? '#e8e8e8' : 'transparent', borderRadius: 4 }} className='table-scroll-button noselect' onClick={() => onPageChange(p)}>{p + 1}</button>
                        )) }
                        { mapIndexes(lastPadding, i => (
                            <div key={'lp'+i} style={{ width: 24 }}></div>
                        )) }

                        <button className='table-scroll-button noselect' onClick={nextPage}><svg width="12" height="12" viewBox="0 0 24 24"><line x1="6" y1="4" x2="18" y2="12" stroke="#888" stroke-width="2" /><line x1="6" y1="20" x2="18" y2="12" stroke="#888" stroke-width="2" /></svg></button>
                        <button style={{ color: '#444' }} className='table-scroll-button noselect' onClick={lastPage}><svg width="12" height="12" viewBox="0 0 24 24"><line x1="6" y1="4" x2="18" y2="12" stroke="#888" stroke-width="2" /><line x1="6" y1="20" x2="18" y2="12" stroke="#888" stroke-width="2" /><line x1="20" y1="0" x2="20" y2="24" stroke="#888" stroke-width="2" /></svg></button>

                    </> ) : ( <>
                        <p className='p8' style={{ color: '#444', textAlign: 'center' }}>{total} rida</p>
                    </> ) }
                </div>
            </div>
        </div>
    );
}

export default ServerTable;
